<script setup lang="ts">
import { useCompanySuggestions } from '@/components/companies/useCompanySuggestions'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

type CompanySuggestionData = App.Data.Company.CompanySuggestionData

defineProps<{
  invalid: boolean
}>()

const emit = defineEmits<{
  suggestionPicked: []
}>()

const name = defineModel<string>({ default: '' })
const handelsregisterEntityId = defineModel<string | null>('handelsregisterEntityId', { default: null })

const { t } = useI18n({ useScope: 'local' })

const { enabled, suggestions, loading, search, reset } = useCompanySuggestions()

const pickedName = ref<string | null>(null)

const query = computed(() => name.value.trim())

const showsPickedMark = computed(() => pickedName.value !== null && name.value === pickedName.value)

const fullWidthInsideInputGroup = 'w-full!'

const pickedMarkClasses =
  "after:text-success-800 after:pointer-events-none after:absolute after:top-1/2 after:right-3 after:-translate-y-1/2 after:text-base after:leading-none after:font-semibold after:content-['✓']"

const fieldClasses = computed(() => [fullWidthInsideInputGroup, showsPickedMark.value ? pickedMarkClasses : ''])

function complete(event: { query: string }): void {
  pickedName.value = null
  handelsregisterEntityId.value = null
  void search(event.query)
}

function pick(event: { value: CompanySuggestionData }): void {
  name.value = event.value.name
  pickedName.value = event.value.name
  handelsregisterEntityId.value = event.value.id
  emit('suggestionPicked')
}

function suggestionLocation(suggestion: CompanySuggestionData): string {
  return [suggestion.postal_code, suggestion.city].filter(Boolean).join(' ')
}

function suggestionRegister(suggestion: CompanySuggestionData): string {
  return [suggestion.court, suggestion.register_type, suggestion.register_number].filter(Boolean).join(' ')
}

defineExpose({
  reset: () => {
    pickedName.value = null
    handelsregisterEntityId.value = null
    reset()
  },
})
</script>

<template>
  <div class="w-full">
    <AutoComplete
      v-if="enabled"
      v-model="name"
      :class="fieldClasses"
      :placeholder="t('placeholder')"
      :invalid="invalid"
      :suggestions="suggestions"
      :loading="loading"
      :delay="300"
      option-label="name"
      autofocus
      :empty-search-message="t('noResults')"
      @complete="complete"
      @option-select="pick">
      <template #option="{ option }: { option: CompanySuggestionData }">
        <div class="flex min-w-0 flex-col gap-1">
          <span class="truncate font-medium">{{ option.name }}</span>
          <span class="text-surface-500 text-sm">
            {{ suggestionLocation(option) }}
            <template v-if="option.register_number">· {{ suggestionRegister(option) }}</template>
          </span>
        </div>
      </template>
      <template #empty>
        <div v-if="loading" class="text-surface-500 flex items-center gap-2 text-sm">
          <i class="pi pi-spinner text-success-800 animate-spin" aria-hidden="true" />
          <span>{{ t('loading') }}</span>
        </div>
        <span v-else>{{ query.length < 2 ? t('minLength') : t('noResults') }}</span>
      </template>
    </AutoComplete>

    <InputText
      v-else
      v-model="name"
      :class="fullWidthInsideInputGroup"
      :placeholder="t('placeholder')"
      :invalid="invalid"
      autofocus />

    <div
      v-if="enabled"
      class="border-success-200 bg-success-50 text-ink-2 mt-2 flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
      role="note">
      <i class="pi pi-sparkles text-success-800 mt-0.5" aria-hidden="true" />

      <div class="space-y-0.5">
        <p class="text-surface-900 font-medium">{{ t('intelligentSearch.title') }}</p>
        <p>{{ t('intelligentSearch.description') }}</p>
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "placeholder": "z. B. Muster Immobilien GmbH",
    "noResults": "Keine passenden Unternehmen gefunden",
    "minLength": "Gib mindestens 2 Zeichen ein.",
    "loading": "Öffentliche Unternehmensdaten werden gesucht...",
    "intelligentSearch": {
      "title": "Intelligente Suche aktiv",
      "description": "Wir ergänzen verfügbare Unternehmensdaten aus öffentlichen Quellen automatisch."
    }
  },
  "en": {
    "placeholder": "e.g. Muster Immobilien GmbH",
    "noResults": "No matching companies found",
    "minLength": "Enter at least 2 characters.",
    "loading": "Searching public company data...",
    "intelligentSearch": {
      "title": "Intelligent search enabled",
      "description": "We automatically enrich available company data from public sources."
    }
  }
}
</i18n>
